import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, Sparkles, Check, Star, Globe, CalendarCheck, Bot, Zap, Wallet, Rocket } from "lucide-react";
import { Reveal, MaskedLine, Stagger, StaggerItem } from "../components/motion";
import { Marquee } from "../components/Marquee";
import { CTAPanel } from "../components/CTAPanel";
import { PRICING, TESTIMONIALS, PROJECTS, PILLARS } from "../data/content";

const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const cardX = useTransform(sx, [-0.5, 0.5], [-14, 14]);
  const cardY = useTransform(sy, [-0.5, 0.5], [-10, 10]);
  const cardX2 = useTransform(sx, [-0.5, 0.5], [18, -18]);
  const cardY2 = useTransform(sy, [-0.5, 0.5], [12, -12]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const orbY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  const onMouseMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="home-hero"
      className="relative overflow-hidden hero-grid-bg pt-[72px]"
    >
      <motion.div style={{ y: orbY }} className="absolute -top-40 right-[-10%] w-[560px] h-[560px] rounded-full bg-indigo/10 blur-[100px] pointer-events-none" />
      <motion.div style={{ y: orbY }} className="absolute top-1/2 left-[-15%] w-[480px] h-[480px] rounded-full bg-mint/10 blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28 grid lg:grid-cols-[1.15fr_1fr] gap-16 items-center relative z-10">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 bg-white border border-ink/8 rounded-full pl-2 pr-4 py-1.5 shadow-sm mb-8"
            data-testid="hero-badge"
          >
            <span className="bg-gradient-to-r from-indigo to-mint text-white text-[11px] font-semibold px-2.5 py-1 rounded-full">Sydney</span>
            <span className="text-xs font-medium text-slate">Digital studio for small business</span>
          </motion.div>

          <h1 className="font-display font-bold text-ink text-4xl sm:text-5xl lg:text-[3.9rem] leading-[1.06]" data-testid="hero-headline">
            <MaskedLine delay={0.2}>Everything your business</MaskedLine>
            <MaskedLine delay={0.32}>needs to <span className="text-gradient">go live</span> —</MaskedLine>
            <MaskedLine delay={0.44}>built fast with AI.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-6 text-slate text-base sm:text-lg leading-relaxed max-w-xl"
            data-testid="hero-subheadline"
          >
            Website, logo, bookings, business email, socials and payments — your entire online presence set up in one affordable package, in weeks not months. No jargon, no lock-ins, no drama.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-primary-cta"
              className="inline-flex items-center gap-2 bg-indigo text-white font-semibold px-7 py-3.5 rounded-full shadow-[0_12px_32px_rgba(91,95,239,0.35)] hover:bg-ink hover:scale-[1.02] transition-[background-color,transform] duration-300 group"
            >
              Get Your Free Digital Health Check
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/pricing"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 bg-white border border-ink/10 text-ink font-semibold px-7 py-3.5 rounded-full hover:border-indigo hover:text-indigo transition-colors duration-300"
            >
              See packages from $799
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
            data-testid="hero-stats"
          >
            {[
              ["7 days", "average Starter launch"],
              ["3 pillars", "launch, grow, automate"],
              ["$799", "all-in starter package"],
            ].map(([big, small]) => (
              <div key={small}>
                <p className="font-display font-bold text-2xl text-ink">{big}</p>
                <p className="text-xs text-slate mt-0.5">{small}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative hidden lg:block h-[520px]" data-testid="hero-visual">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            style={{ x: cardX, y: cardY }}
            className="absolute top-4 left-0 w-[340px] bg-white rounded-3xl shadow-[0_24px_64px_rgba(20,22,46,0.14)] border border-ink/5 overflow-hidden"
          >
            <div className="flex items-center gap-1.5 px-4 py-3 border-b border-ink/5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              <span className="ml-3 text-[10px] text-slate bg-cloud rounded-full px-3 py-1">yourbusiness.com.au</span>
            </div>
            <div className="p-5">
              <div className="h-3 w-2/3 rounded-full bg-ink/85 mb-2.5" />
              <div className="h-3 w-1/2 rounded-full bg-ink/85 mb-5" />
              <div className="h-2 w-full rounded-full bg-slate/25 mb-2" />
              <div className="h-2 w-5/6 rounded-full bg-slate/25 mb-5" />
              <div className="flex gap-2">
                <div className="h-8 w-24 rounded-full bg-gradient-to-r from-indigo to-mint" />
                <div className="h-8 w-20 rounded-full border border-ink/10" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            style={{ x: cardX2, y: cardY2 }}
            className="absolute top-[210px] right-0 w-[240px] bg-ink rounded-3xl shadow-[0_24px_64px_rgba(20,22,46,0.3)] p-5 animate-float-slow"
          >
            <div className="flex items-center gap-2.5 mb-4">
              <span className="w-9 h-9 rounded-xl bg-mint/20 flex items-center justify-center">
                <CalendarCheck className="w-4 h-4 text-mint" />
              </span>
              <div>
                <p className="text-white text-xs font-semibold">New booking</p>
                <p className="text-white/50 text-[10px]">just now</p>
              </div>
            </div>
            <p className="text-white/80 text-xs leading-relaxed">Tue 14 July, 10:30am — Consultation confirmed</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            style={{ x: cardX, y: cardY2 }}
            className="absolute bottom-2 left-10 w-[260px] bg-white rounded-3xl shadow-[0_24px_64px_rgba(20,22,46,0.14)] border border-ink/5 p-5"
          >
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-9 h-9 rounded-xl bg-indigo/10 flex items-center justify-center">
                <Bot className="w-4 h-4 text-indigo" />
              </span>
              <p className="text-ink text-xs font-semibold">AI chatbot</p>
            </div>
            <div className="bg-cloud rounded-2xl rounded-bl-md px-3.5 py-2.5">
              <p className="text-ink/80 text-xs leading-relaxed">"Yes, we're open Saturdays! Would you like to book?"</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ServicesOverview = () => (
  <section className="py-24 sm:py-32 px-5 sm:px-8" data-testid="services-overview">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">What we do</p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-ink max-w-xl leading-[1.1]">
            One studio. Three ways we move you forward.
          </h2>
          <Link to="/services" data-testid="services-overview-link" className="inline-flex items-center gap-2 text-indigo font-semibold text-sm hover:gap-3 transition-[gap] duration-300 shrink-0">
            Explore all services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Reveal>
      <Stagger className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {PILLARS.map((pillar, i) => (
          <StaggerItem key={pillar.id} className="h-full">
            <Link
              to={`/services#${pillar.id}`}
              data-testid={`pillar-card-${pillar.id}`}
              className={`group block h-full rounded-[2rem] p-8 sm:p-9 transition-[box-shadow,transform] duration-300 hover:-translate-y-1.5 ${
                i === 1
                  ? "bg-ink text-white shadow-[0_24px_56px_rgba(20,22,46,0.25)] hover:shadow-[0_32px_64px_rgba(20,22,46,0.35)]"
                  : "bg-white border border-ink/5 shadow-sm hover:shadow-[0_20px_48px_rgba(20,22,46,0.1)]"
              }`}
            >
              <div className="flex items-center justify-between mb-7">
                <span className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                  i === 1 ? "bg-white/10" : "bg-gradient-to-br from-indigo/10 to-mint/10"
                }`}>
                  <pillar.icon className={`w-5 h-5 ${i === 1 ? "text-mint" : "text-indigo"}`} />
                </span>
                <span className={`font-display font-bold text-4xl ${i === 1 ? "text-white/10" : "text-ink/8"}`}>
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className={`font-display font-bold text-2xl mb-2 ${i === 1 ? "text-white" : "text-ink"}`}>{pillar.name}</h3>
              <p className={`text-sm mb-7 leading-relaxed ${i === 1 ? "text-white/55" : "text-slate"}`}>{pillar.tagline}</p>
              <ul className="space-y-3">
                {pillar.services.map((s) => (
                  <li key={s.title} className="flex items-center gap-3">
                    <s.icon className={`w-4 h-4 shrink-0 ${i === 1 ? "text-mint" : "text-indigo"}`} />
                    <span className={`text-sm font-medium ${i === 1 ? "text-white/85" : "text-ink/80"}`}>{s.title}</span>
                  </li>
                ))}
              </ul>
              <span className={`mt-8 inline-flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-[gap] duration-300 ${
                i === 1 ? "text-mint" : "text-indigo"
              }`}>
                See details <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const MANIFESTO = [
  {
    icon: Zap,
    num: "01",
    title: "AI speed, human craft",
    text: "We use AI to do in days what traditional agencies quote months for — then real designers and developers polish every pixel before it goes live.",
  },
  {
    icon: Wallet,
    num: "02",
    title: "Priced for small business",
    text: "Fixed packages from $799. No hourly billing surprises, no confusing tech-speak, no lock-in contracts. You know exactly what you get and what it costs.",
  },
  {
    icon: Rocket,
    num: "03",
    title: "Live in weeks, not months",
    text: "Most projects launch in one to three weeks. Because every week without a proper online presence is a week of customers finding your competitors instead.",
  },
];

const WhyAvora = () => (
  <section className="bg-ink grain py-24 sm:py-32 px-5 sm:px-8" data-testid="why-avora">
    <div className="max-w-7xl mx-auto relative z-10">
      <Reveal>
        <p className="text-mint font-semibold text-sm uppercase tracking-widest mb-4">Why Avora</p>
        <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white max-w-2xl leading-[1.1] mb-16">
          Big-agency polish, small-business sense.
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 rounded-3xl overflow-hidden">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.num} delay={i * 0.12} className="bg-ink">
            <div className="p-9 sm:p-11 h-full" data-testid={`manifesto-chapter-${m.num}`}>
              <div className="flex items-center justify-between mb-8">
                <span className="font-display font-bold text-5xl text-white/10">{m.num}</span>
                <span className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <m.icon className="w-5 h-5 text-mint" />
                </span>
              </div>
              <h3 className="font-display font-semibold text-white text-xl mb-3">{m.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed">{m.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WorkPreview = () => (
  <section className="py-24 sm:py-32 px-5 sm:px-8" data-testid="work-preview">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">Recent work</p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-ink max-w-xl leading-[1.1]">
            Small businesses, now looking anything but small.
          </h2>
          <Link to="/work" data-testid="work-preview-link" className="inline-flex items-center gap-2 text-indigo font-semibold text-sm hover:gap-3 transition-[gap] duration-300 shrink-0">
            See all case studies <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Reveal>
      <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PROJECTS.slice(0, 3).map((p) => (
          <StaggerItem key={p.name}>
            <Link to="/work" data-testid={`work-card-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group block bg-white rounded-3xl border border-ink/5 overflow-hidden shadow-sm hover:shadow-[0_20px_48px_rgba(20,22,46,0.1)] hover:-translate-y-1.5 transition-[box-shadow,transform] duration-300">
              <div className="grid grid-cols-2">
                <div className="bg-cloud p-5 min-h-[130px] flex flex-col justify-center">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate mb-2">Before</p>
                  <p className="text-xs text-slate leading-relaxed">{p.before}</p>
                </div>
                <div className="bg-gradient-to-br from-indigo to-mint p-5 min-h-[130px] flex flex-col justify-center">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-white/70 mb-2">After</p>
                  <p className="text-xs text-white leading-relaxed font-medium">{p.after}</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-xs text-slate mb-1">{p.industry}</p>
                <h3 className="font-display font-semibold text-ink">{p.name}</h3>
              </div>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>

      <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-16">
        {TESTIMONIALS.map((t) => (
          <StaggerItem key={t.name}>
            <figure data-testid={`testimonial-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="h-full bg-white rounded-3xl border border-ink/5 p-8 shadow-sm flex flex-col">
              <div className="flex gap-1 mb-5">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-mint text-mint" />)}
              </div>
              <blockquote className="text-ink/80 text-sm leading-relaxed flex-1">"{t.quote}"</blockquote>
              <figcaption className="mt-6">
                <p className="font-display font-semibold text-ink text-sm">{t.name}</p>
                <p className="text-slate text-xs mt-0.5">{t.role}</p>
              </figcaption>
            </figure>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const PricingPreview = () => (
  <section className="py-24 sm:py-32 px-5 sm:px-8 bg-white" data-testid="pricing-preview">
    <div className="max-w-7xl mx-auto">
      <Reveal className="text-center mb-14">
        <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">Pricing</p>
        <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-ink leading-[1.1]">
          Honest packages. Fixed prices.
        </h2>
        <p className="text-slate mt-4 max-w-xl mx-auto text-base">One-off launch packages, plus monthly growth, automation and care plans from $99/month. No lock-ins, no surprises.</p>
      </Reveal>
      <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
        {PRICING.map((tier) => (
          <StaggerItem key={tier.name} className="h-full">
            <div
              data-testid={`pricing-card-${tier.name.toLowerCase()}`}
              className={`relative h-full rounded-3xl p-8 flex flex-col ${
                tier.featured
                  ? "bg-ink text-white shadow-[0_24px_64px_rgba(20,22,46,0.3)] md:-translate-y-3"
                  : "bg-cloud border border-ink/5"
              }`}
            >
              {tier.featured && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo to-mint text-white text-xs font-semibold px-4 py-1.5 rounded-full inline-flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> Most popular
                </span>
              )}
              <h3 className={`font-display font-semibold text-lg ${tier.featured ? "text-white" : "text-ink"}`}>{tier.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className={`font-display font-bold text-4xl ${tier.featured ? "text-white" : "text-ink"}`}>${tier.price.toLocaleString()}</span>
                <span className={`text-xs ${tier.featured ? "text-white/50" : "text-slate"}`}>one-off</span>
              </div>
              <p className={`text-sm mt-2 ${tier.featured ? "text-white/60" : "text-slate"}`}>{tier.tagline}</p>
              <ul className="mt-7 space-y-3 flex-1">
                {tier.features.slice(0, 4).map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tier.featured ? "text-mint" : "text-indigo"}`} />
                    <span className={tier.featured ? "text-white/80" : "text-ink/75"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={`/contact?plan=${encodeURIComponent(`${tier.name} Package`)}`}
                data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                className={`mt-8 inline-flex items-center justify-center gap-2 font-semibold px-6 py-3 rounded-full transition-colors duration-300 ${
                  tier.featured
                    ? "bg-gradient-to-r from-indigo to-mint text-white hover:opacity-90"
                    : "bg-ink text-white hover:bg-indigo"
                }`}
              >
                {tier.cta}
              </Link>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
      <Reveal className="text-center mt-10">
        <Link to="/pricing" data-testid="pricing-compare-link" className="inline-flex items-center gap-2 text-indigo font-semibold text-sm hover:gap-3 transition-[gap] duration-300">
          Compare all features & add-ons <ArrowRight className="w-4 h-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <ServicesOverview />
      <WhyAvora />
      <WorkPreview />
      <PricingPreview />
      <CTAPanel />
    </>
  );
}
